import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Image } from 'foundations-components/transitional/components/Image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReviewRatingInput, ReviewSelectInput, ReviewTextInput } from 'trellis-ecosystem';
import { PENDING } from '../../constants/UnreviewableReason';
import { useGetCatalogServices } from '../../hooks/useGetCatalogServices';
import { EcosystemReviewModal } from './EcosystemReviewModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROFILE_SUBSCREEN = 'solutions partner profile review modal';
const VALIDATION_SCHEMA = {
  content: {
    TITLE: {
      required: true
    },
    REVIEW: {
      required: true,
      minCharacters: 75,
      maxCharacters: 3000
    }
  },
  ratings: {
    OVERALL: {
      required: true
    }
  },
  tags: {
    CATALOG_SERVICES: {
      required: true,
      minSelected: 1,
      maxSelected: 10
    }
  }
};
export function SolutionsPartnerProfileReviewModal({
  editedReview,
  iconUrl,
  isPublic,
  name,
  onClose,
  onSuccess,
  onTrackInteraction,
  profileId,
  unreviewableReason
}) {
  const {
    catalogServices
  } = useGetCatalogServices({});
  const isEditing = !!editedReview;
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: 0
    },
    tags: {
      CATALOG_SERVICES: []
    }
  };
  let defaultReview;
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`PROFILE_${profileId}_review`));
    defaultReview = storedReview || emptyReview;
  } catch (__error) {
    defaultReview = emptyReview;
  }
  if (editedReview) {
    const {
      answers: content,
      ratings,
      tags
    } = editedReview.toJSON();
    defaultReview = {
      content,
      ratings,
      tags
    };
  }
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [completedAnswerFields, setCompletedAnswerFields] = useState(Object.entries(defaultReview.content).filter(([field, value]) => field && !!value).map(([field]) => field));
  const openReviewWizardTracked = useRef(false);
  useEffect(() => {
    if (!openReviewWizardTracked.current) {
      onTrackInteraction({
        action: isEditing ? 'open edit review wizard' : 'open review wizard',
        subscreen: PROFILE_SUBSCREEN
      });
      openReviewWizardTracked.current = true;
    }
  }, [isEditing, onTrackInteraction]);
  const alertedAndTrackedUnreviewableReason = useRef(false);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}` && alertedAndTrackedUnreviewableReason.current === false) {
      onTrackInteraction({
        action: 'review wizard auto-dismissed',
        subscreen: PROFILE_SUBSCREEN,
        value: unreviewableReason
      });
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.error.ReviewError.GENERIC_UNREVIEWABLE"
        }),
        type: 'danger'
      });
      alertedAndTrackedUnreviewableReason.current = true;
    }
  }, [isEditing, onTrackInteraction, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction({
      action: 'submit review',
      subscreen: PROFILE_SUBSCREEN
    });
    sessionStorage.removeItem(`PROFILE_${profileId}_review`);
    onSuccess();
  }, [onSuccess, profileId, onTrackInteraction]);
  const handleClose = useCallback(() => {
    onTrackInteraction({
      action: 'dismiss review wizard',
      subscreen: PROFILE_SUBSCREEN
    });
    onClose();
  }, [onClose, onTrackInteraction]);
  const onChange = useCallback(review => {
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction({
        action: 'set overall rating',
        subscreen: PROFILE_SUBSCREEN,
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction({
          action: 'add review answer',
          subscreen: PROFILE_SUBSCREEN,
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`PROFILE_${profileId}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, isEditing, profileId, onTrackInteraction, overallRating]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  const serviceOptions = (catalogServices || []).map(service => ({
    value: service.id,
    text: service.name
  }));
  return /*#__PURE__*/_jsxs(EcosystemReviewModal, {
    defaultReview: defaultReview,
    editedReview: editedReview,
    width: 600,
    headerContent: /*#__PURE__*/_jsx(Text, {
      as: "h2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-ui-reviews.write.${isEditing ? 'edit' : 'new'}`
      })
    }),
    offeringId: profileId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    ratingSection: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        paddingBlock: "200",
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "row",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(Image, {
            alt: name,
            blockSize: "40px",
            marginBlock: "200",
            responsive: false,
            src: iconUrl
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        as: "span",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h5",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.wizard.profile.header",
            options: {
              name
            }
          })
        })
      }), /*#__PURE__*/_jsx(ReviewRatingInput, {
        fieldKey: "OVERALL",
        label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.OVERALL')
      })]
    }),
    showLegalCheckbox: true,
    reviewType: "PROFILE",
    requireEditReason: true,
    validationSchema: VALIDATION_SCHEMA,
    children: [/*#__PURE__*/_jsx(ReviewSelectInput, {
      fieldKey: "CATALOG_SERVICES",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.CATALOG_SERVICES'),
      multi: true,
      options: serviceOptions,
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.CATALOG_SERVICES')
    }), /*#__PURE__*/_jsx(ReviewTextInput, {
      fieldKey: "TITLE",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.TITLE'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.TITLE'),
      resize: false,
      rows: 1
    }), /*#__PURE__*/_jsx(ReviewTextInput, {
      fieldKey: "REVIEW",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.REVIEW'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.REVIEW', {
        name
      }),
      rows: 4
    })]
  });
}